/**
 * RateLimitPanel — G004 레이트리밋 데모
 * R12 #332
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { showcaseApi } from '../api/showcaseApi';

interface CallResult {
  index: number;
  status: number | 'error';
  allowed: boolean;
  remaining: number | null;
  retryAfterMs: number | null;
}

export function RateLimitPanel() {
  const { t } = useTranslation();
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState<number | null>(null);
  const [total, setTotal] = useState<number | null>(null);
  const [results, setResults] = useState<CallResult[]>([]);
  const [resetNote, setResetNote] = useState(false);

  async function runCalls(n: number) {
    setRunning(true);
    setProgress(0);
    setTotal(n);
    setResults([]);
    setResetNote(false);

    const newResults: CallResult[] = [];

    for (let i = 0; i < n; i++) {
      setProgress(i + 1);
      try {
        const res = await showcaseApi.rateLimitDemo();
        newResults.push({
          index: i + 1,
          status: res.allowed ? 200 : 429,
          allowed: res.allowed,
          remaining: res.remaining,
          retryAfterMs: res.retryAfterMs,
        });
        setResults([...newResults]);
      } catch (err: unknown) {
        const apiErr = err as { statusCode?: number; message?: string };
        const statusCode = apiErr?.statusCode ?? 429;
        newResults.push({
          index: i + 1,
          status: statusCode,
          allowed: false,
          remaining: 0,
          retryAfterMs: null,
        });
        setResults([...newResults]);
      }
    }

    setRunning(false);

    // Show reset note after 1.5s
    setTimeout(() => setResetNote(true), 1500);
  }

  const lastRetryAfter = results.find((r) => !r.allowed && r.retryAfterMs != null)?.retryAfterMs;
  const allowedCount = results.filter((r) => r.allowed).length;
  const blockedCount = results.filter((r) => !r.allowed).length;
  const isDone = !running && results.length > 0;

  return (
    <section aria-labelledby="rate-limit-panel-title">
      <h2
        id="rate-limit-panel-title"
        style={{
          fontSize: 'var(--font-size-lg)',
          fontWeight: 700,
          color: 'var(--color-text-primary)',
          marginBottom: 'var(--spacing-2)',
          marginTop: 0,
        }}
      >
        {t('showcase.rateLimit.title')}
      </h2>
      <p
        style={{
          color: 'var(--color-text-secondary)',
          fontSize: 'var(--font-size-sm)',
          marginBottom: 'var(--spacing-4)',
        }}
      >
        G004 — eraf-gateway
      </p>

      {/* Hint paragraph */}
      {/* #423: hint 60s 윈도 반영 */}
      <p
        style={{
          fontSize: 'var(--font-size-sm)',
          color: 'var(--color-text-secondary)',
          lineHeight: 1.7,
          padding: 'var(--spacing-3)',
          background: 'var(--color-surface-elevated)',
          border: '1px solid var(--color-surface-border)',
          borderRadius: 'var(--radius-sm)',
          marginBottom: 'var(--spacing-4)',
        }}
      >
        {t('showcase.rateLimit.hint')}
      </p>

      {/* Action buttons */}
      <div style={{ display: 'flex', gap: 'var(--spacing-3)', marginBottom: 'var(--spacing-4)' }}>
        <button
          type="button"
          onClick={() => runCalls(10)}
          disabled={running}
          style={{
            padding: 'var(--spacing-2) var(--spacing-4)',
            background: running ? 'var(--color-surface-muted)' : 'var(--color-brand-primary)',
            color: running ? 'var(--color-text-muted)' : 'var(--color-text-on-brand)',
            border: 'none',
            borderRadius: 'var(--radius-sm)',
            fontWeight: 700,
            fontSize: 'var(--font-size-sm)',
            cursor: running ? 'not-allowed' : 'pointer',
          }}
        >
          {t('showcase.rateLimit.fire10')}
        </button>
        <button
          type="button"
          onClick={() => runCalls(11)}
          disabled={running}
          style={{
            padding: 'var(--spacing-2) var(--spacing-4)',
            background: running ? 'var(--color-surface-muted)' : 'var(--color-status-error)',
            color: running ? 'var(--color-text-muted)' : '#fff',
            border: 'none',
            borderRadius: 'var(--radius-sm)',
            fontWeight: 700,
            fontSize: 'var(--font-size-sm)',
            cursor: running ? 'not-allowed' : 'pointer',
          }}
        >
          {t('showcase.rateLimit.fire11')}
        </button>
      </div>

      {/* Progress */}
      {running && progress !== null && total !== null && (
        <p
          role="status"
          aria-live="polite"
          style={{
            fontSize: 'var(--font-size-sm)',
            color: 'var(--color-text-secondary)',
            marginBottom: 'var(--spacing-3)',
          }}
        >
          {t('showcase.rateLimit.progress', { current: progress, total })}
        </p>
      )}

      {/* Results timeline */}
      {results.length > 0 && (
        <div style={{ marginBottom: 'var(--spacing-4)' }}>
          {/* Summary line — shown once done */}
          {isDone && (
            <p
              role="status"
              aria-live="polite"
              style={{
                fontSize: 'var(--font-size-sm)',
                fontWeight: 700,
                marginBottom: 'var(--spacing-2)',
                color: blockedCount > 0 ? 'var(--color-status-error)' : 'var(--color-status-success)',
              }}
            >
              {t('showcase.rateLimit.summary', { allowed: allowedCount, blocked: blockedCount })}
            </p>
          )}

          {/* Call tiles: green=200, red=429 */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--spacing-1)' }}>
            {results.map((r) => (
              <div
                key={r.index}
                title={`#${r.index}: HTTP ${r.status}${r.remaining != null ? ` (remaining ${r.remaining})` : ''}`}
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: 'var(--radius-sm)',
                  background: r.allowed
                    ? 'var(--color-status-success)'
                    : 'var(--color-status-error)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 9,
                  fontFamily: 'var(--font-family-mono)',
                  color: '#fff',
                  fontWeight: 700,
                  cursor: 'default',
                  flexDirection: 'column',
                  gap: 1,
                }}
                aria-label={`Call ${r.index}: HTTP ${r.status}`}
              >
                <span style={{ fontSize: 8, opacity: 0.8 }}>#{r.index}</span>
                <span>{r.status}</span>
              </div>
            ))}
          </div>

          {/* Retry-After notice */}
          {lastRetryAfter !== undefined && lastRetryAfter !== null && (
            <p
              style={{
                fontSize: 'var(--font-size-sm)',
                color: 'var(--color-status-error)',
                marginTop: 'var(--spacing-2)',
              }}
            >
              {t('showcase.rateLimit.retryAfter', { ms: lastRetryAfter })}
            </p>
          )}

          {/* Reset note */}
          {resetNote && !running && (
            <p
              role="status"
              aria-live="polite"
              style={{
                fontSize: 'var(--font-size-sm)',
                color: 'var(--color-text-muted)',
                marginTop: 'var(--spacing-2)',
                fontStyle: 'italic',
              }}
            >
              {t('showcase.rateLimit.resetHint')}
            </p>
          )}
        </div>
      )}
    </section>
  );
}
